import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { postRequest } from '../../lib/api';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Paper,
  TextField,
  Typography,
  Grid,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

export default function RegisterPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const password = watch('password');

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      // Prepare registration data - backend handles role-specific logic
      const registrationData = {
        email: data.email,
        password: data.password,
        forename: data.firstName,
        surname: data.lastName,
        username: data.username,
        phone_number: data.phone_number || '',
        role: 'SUPERADMIN', // Backend automatically sets hierarchyLevel, status, etc. based on role
      };

      const response = await postRequest('auth/register', registrationData);

      if (response) {
        toast.success('SUPERADMIN registration successful! Please login.');
        setTimeout(() => {
          navigate('/login');
        }, 1500);
      }
    } catch (err) {
      console.error('Registration error:', err);
      // Error already handled by axios interceptor
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xl">
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        sx={{ minHeight: '100vh' }}
      >
        <Grid item xs={12}>
          <Paper elevation={6} sx={{ p: 4, borderRadius: 3 }}>
            <Typography variant="h5" align="center" gutterBottom>
              Universal - SUPERADMIN Registration
            </Typography>
            <Typography variant="body2" align="center" color="text.secondary" mb={2}>
              Create a SUPERADMIN account
            </Typography>

            <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
              {/* First Name Field */}
              <TextField
                label="First Name"
                fullWidth
                margin="normal"
                type="text"
                {...register('firstName', {
                  required: 'First name is required',
                  minLength: {
                    value: 2,
                    message: 'First name must be at least 2 characters',
                  },
                })}
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
                disabled={loading}
              />

              {/* Last Name Field */}
              <TextField
                label="Last Name"
                fullWidth
                margin="normal"
                type="text"
                {...register('lastName', {
                  required: 'Last name is required',
                  minLength: {
                    value: 2,
                    message: 'Last name must be at least 2 characters',
                  },
                })}
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
                disabled={loading}
              />

              {/* Username Field */}
              <TextField
                label="Username"
                fullWidth
                margin="normal"
                type="text"
                {...register('username', {
                  required: 'Username is required',
                  minLength: {
                    value: 3,
                    message: 'Username must be at least 3 characters',
                  },
                  pattern: {
                    value: /^[a-zA-Z0-9_]+$/,
                    message: 'Username can only contain letters, numbers, and underscores',
                  },
                })}
                error={!!errors.username}
                helperText={errors.username?.message}
                disabled={loading}
              />

              {/* Email Field */}
              <TextField
                label="Email"
                fullWidth
                margin="normal"
                type="email"
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                })}
                error={!!errors.email}
                helperText={errors.email?.message}
                disabled={loading}
              />

              {/* Phone Number Field */}
              <TextField
                label="Phone Number"
                fullWidth
                margin="normal"
                type="tel"
                {...register('phone_number', {
                  required: 'Phone number is required',
                  pattern: {
                    value: /^[0-9+\-\s()]+$/,
                    message: 'Invalid phone number format',
                  },
                })}
                error={!!errors.phone_number}
                helperText={errors.phone_number?.message}
                disabled={loading}
              />

              {/* Password Field with Toggle */}
              <FormControl fullWidth margin="normal" variant="outlined">
                <InputLabel htmlFor="password">Password</InputLabel>
                <OutlinedInput
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  {...register('password', {
                    required: 'Password is required',
                    minLength: {
                      value: 8,
                      message: 'Password must be at least 8 characters',
                    },
                    pattern: {
                      value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                      message: 'Password must contain at least one uppercase letter, one lowercase letter, and one number',
                    },
                  })}
                  error={!!errors.password}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        onClick={() => setShowPassword((prev) => !prev)}
                        disabled={loading}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label="Password"
                  disabled={loading}
                />
                {errors.password && (
                  <Typography color="error" variant="caption">
                    {errors.password.message}
                  </Typography>
                )}
              </FormControl>

              {/* Confirm Password Field with Toggle */}
              <FormControl fullWidth margin="normal" variant="outlined">
                <InputLabel htmlFor="confirmPassword">Confirm Password</InputLabel>
                <OutlinedInput
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  {...register('confirmPassword', {
                    required: 'Please confirm your password',
                    validate: (value) =>
                      value === password || 'Passwords do not match',
                  })}
                  error={!!errors.confirmPassword}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        disabled={loading}
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label="Confirm Password"
                  disabled={loading}
                />
                {errors.confirmPassword && (
                  <Typography color="error" variant="caption">
                    {errors.confirmPassword.message}
                  </Typography>
                )}
              </FormControl>

              {/* Submit Button */}
              <Button
                type="submit"
                variant="contained"
                className='red-bg red-button text-capitalize'
                fullWidth
                sx={{ mt: 3, py: 1.5, fontWeight: 'bold' }}
                disabled={loading}
                startIcon={loading && <CircularProgress size={20} />}
              >
                {loading ? 'Registering...' : 'Register as SUPERADMIN'}
              </Button>

              {/* Login Link */}
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Already have an account?{' '}
                  <Link
                    to="/login"
                    component={RouterLink}
                    underline="hover"
                    sx={{ color: '#1976d2', fontWeight: 'bold' }}
                  >
                    Login here
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}
